import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "../../../components/layout/MainLayout";
import NursingRecordForm from "../NursingRecord/NursingRecordForm";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { createNursingRecord, fetchNurses } from "../../../store/slices/nursingRecordsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddNursingRecord = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};
    const { nurses } = useSelector((state) => state.nursingRecords);

    const { isLoading } = useSelector(
        (state) => state.individualisedRehabPlan
    );

    const { user } = useSelector((state) => state.auth);

    // master form fields
    const [formData, setFormData] = useState({
        patientId, // initialize
        caseId,    // initialize
        name: "",
        mrNo: "",
        roomNo: "",
        consultant: "",
    });

    useEffect(() => {
        dispatch(fetchNurses());
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                            mrNo: patient.mr_no || "",
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient:", err));
        }
    }, [dispatch, patientId, user?.name]);

    // IMPORTANT: rows state passed to child (initialize with one empty row)
    const [rows, setRows] = useState([
        { id: Date.now(), date: "", hours: "00:00:AM-00:00:PM", remarks: "", nurse: "" },
    ]);

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.name || !formData.mrNo) {
            setErrors({
                name: !formData.name ? "Name is required." : "",
                mrNo: !formData.mrNo ? "M.R No is required." : "",
            });

            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields before submitting.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        // map rows -> payload
        const nursingRecordsPayload = (rows || []).map((row) => ({
            date: row.date || null,
            hours: row.hours || null,
            remarks: row.remarks || null,
            nurse: row.nurse || null,
            nurse_id: row.nurse_id || null,
        }));

        const payload = {
            patient_id: formData.patientId || null,
            case_id: formData.caseId || null,
            name: formData.name,
            mr_no: formData.mrNo,
            room_no: formData.roomNo,
            consultant: formData.consultant,
            nursing_records: nursingRecordsPayload,
        };

        try {
            const res = await dispatch(createNursingRecord(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Nursing Record added successfully.",
                icon: "success",
                timer: 1600,
                showConfirmButton: false,
            });

            navigate(`/phase05/nursing-records/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: err?.message || "Failed to add Nursing Record.",
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                    <div className="card-body">
                        {/* Validation Errors */}
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors)
                                    .filter(Boolean)
                                    .map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <NursingRecordForm
                                formData={formData}
                                handleChange={handleChange}
                                rows={rows}
                                nurses={nurses}
                                setRows={setRows}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-12 d-flex justify-content-end mt-4">
                                    <div className="col-md-2 d-flex justify-content-end">
                                        <button
                                            type="submit"
                                            className="btn btn-primary px-4"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <i className="ri-loader-4-line me-1 animate-spin"></i> Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-save-2-line me-1"></i> Save
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddNursingRecord;
